import { deriveScoreTextStyle } from '#app/utils/application-detail-v2/vendor/score-bands'
/**
 * THE score rendering switch for every surface that shows an applicant /
 * household score (dashboard cards, listing rows, pipeline, report headers):
 * band mode → the shared ScoreBandBar; numeric mode → the raw `NN/100` with
 * the value tinted by the active band's hue (deriveScoreTextStyle — same
 * defs as the bar and the PDF, so surfaces can never disagree).
 *
 * The mode comes from the per-landlord `score_display` setting
 * (app/utils/landlord-settings/registry.ts), resolved server-side from the
 * DATA-OWNING tenant and threaded through the surface's DTO — this component
 * never decides the mode, and callers still decide WHETHER a score shows at
 * all (pre-screening hides it entirely; that rule is unchanged).
 *
 * Pending (null score) renders the faded band bar in BOTH modes — a numeric
 * surface with no score yet shows the same pending state as everyone else.
 */
import { cn } from '#app/utils/misc'
import { type BandBarSize, ScoreBandBar, ScoreGaugeBar } from './ScoreBandBar'

export type { ScoreDisplayMode } from '#app/utils/application-detail-v2/vendor/landlord-settings-registry'

export function ScoreDisplay({
	score,
	display,
	size = 'sm',
	fullWidthBar = false,
	withBar = false,
	className,
}: {
	/** Rounded 0–100 score; null = pending. */
	score: number | null
	/** Per-landlord render mode (DTO-provided) — 'band' is the default. */
	display: 'band' | 'numeric'
	size?: BandBarSize
	/** Stretch the band/pending bar to full width WITHOUT changing its height
	 * (height still follows `size`) — the pipeline Ready-For-Review cards want a
	 * full-width but thin (h-1.5) band bar. Band/pending mode only. */
	fullWidthBar?: boolean
	/** Stack a band-tinted gauge bar UNDER the NN/100 (client mock 2026-07-22).
	 * Numeric mode only, and the number goes dark since the bar now carries the
	 * colour. On for the report header (lg) and, since 2026-07-24, the dashboard
	 * cards + pipeline (client: the dashboard score should show the same bar as
	 * everywhere else). Off for the listing rows — they render the combined
	 * ScoreStatusPill in numeric mode instead. Size drives the layout: lg =
	 * full-width centred gauge under the big number; sm = compact left-aligned
	 * block with a tighter gap. */
	withBar?: boolean
	className?: string
}) {
	const textStyle = display === 'numeric' ? deriveScoreTextStyle(score) : null
	if (textStyle === null) {
		// Band mode, or numeric with no usable score yet (pending band bar).
		// Report header (lg): each enterprise band segment is 132px (ScoreBandBar).
		return (
			<ScoreBandBar
				score={score}
				size={size}
				fullWidth={fullWidthBar}
				className={className}
			/>
		)
	}
	// Same clamp/round as the band derivation so bar and number always agree.
	const value = Math.min(100, Math.max(0, Math.round(score as number)))
	const numberRow = (
		<div
			className={cn(
				'flex items-baseline',
				size === 'lg' ? 'gap-1.5' : 'gap-0.5',
				// When the gauge carries the colour the number reads dark (mock);
				// without a gauge the number itself is band-tinted.
				withBar ? undefined : className,
			)}
			role="img"
			aria-label={`Score ${value} out of 100`}
		>
			<span
				className={cn(
					'font-medium',
					size === 'lg' ? 'text-5xl' : 'text-[30px] leading-none',
					withBar ? 'text-[#32302F]' : textStyle.textClass,
				)}
			>
				{value}
			</span>
			<span
				className={cn(
					'font-medium text-[#747474]',
					size === 'lg' ? 'text-3xl' : 'text-[17px] leading-none',
				)}
			>
				/100
			</span>
		</div>
	)
	if (!withBar) return numberRow
	return (
		<div
			className={cn(
				'flex flex-col items-center',
				// lg (report header): fixed 215px gauge under the big number.
				// sm (dashboard cards): compact block. The pipeline passes
				// fullWidthBar → span the card and centre the number + full-width bar.
				size === 'lg'
					? 'w-[215px] gap-3'
					: fullWidthBar
						? 'w-full gap-1.5'
						: 'w-24 gap-1.5',
				className,
			)}
		>
			{numberRow}
			<ScoreGaugeBar score={value} size={size} />
		</div>
	)
}
